import { useQuery } from '@tanstack/react-query';
import { bidKeys, CLOSED_EOD_GATE, type EodGate } from '@tempmee/awards';
import { useAppSelector } from '~/hooks';
import { getEodBidGateService } from '../services/GetEodBidGate';

/**
 * Fetches the CS-5 EOD gate for a given job — whether the EOD flow should
 * show a Best-in-Dental feedback step, and for whom.
 *
 * FAIL-OPEN: the query itself never errors. `getEodBidGateService` catches
 * every failure and resolves to `CLOSED_EOD_GATE`, and `placeholderData`
 * shows the same closed gate while the query is in flight — so the EOD flow
 * never waits on, or breaks because of, this gate.
 */
export const useEodBidGate = (
  cycleId: string | undefined,
  applicantUserId: string | undefined,
  /**
   * Overrides the `x-office` header (and cache scope) — pending-list rows
   * must ask as the ROW's office, not the selected one. Omit in the EOD
   * flow, where the selected office is the reviewer.
   */
  officeId?: string,
) => {
  // The gate answer depends on WHO is asking (x-office header) — scope the
  // cache so office A's submitted feedback can't leave a closed gate that a
  // sibling office B reads as "nothing pending" (GOTU-6571 via cache).
  const selectedOfficeId = useAppSelector((state) => state.user.selectedOfficeId);
  const scopeOfficeId = officeId ?? selectedOfficeId;

  return useQuery<EodGate>({
    queryKey: [...bidKeys.all, 'eod-gate', cycleId, applicantUserId, scopeOfficeId],
    queryFn: () => getEodBidGateService(cycleId!, applicantUserId!, officeId),
    retry: false,
    refetchOnWindowFocus: false,
    enabled: !!cycleId && !!applicantUserId,
    placeholderData: CLOSED_EOD_GATE,
  });
};
